/**
 * The board's terrain layer (`projects/terrain-tile-art.md` Phase 4, `decisions/0192`): one Sprite
 * per 16×16-cell chunk, its texture painted by `paintChunk` on an OffscreenCanvas.
 *
 * - **Dirty repaint.** `setTerrain` diffs against the last terrain and walls (`dirtyChunks`) and
 *   repaints only the chunks whose pixels can change. Chunks paint one per task so the board stays
 *   responsive; the previous textures stay on screen until every dirty chunk is ready, then all
 *   swap in one frame. A change that lands mid-build joins the build. A first paint has nothing to keep
 *   on screen, so it shows each chunk as it lands, the visible ones first.
 * - **Density.** Every chunk holds a 1× texture. Once the view settles wanting more than 1.5
 *   device px per board px (`onViewSettled`), visible chunks repaint at 2×, nearest the centre
 *   first, under a byte cap with least-recently-seen eviction. 2× is the ceiling (`chunks.ts`).
 * - **Timing.** A build is timed from `setTerrain` (or the atlas arriving) to the end of the first
 *   render after the swap, so the canvas flush and the GPU upload are inside the number.
 */
import { type Container, type Ticker, ImageSource, Sprite, Texture, UPDATE_PRIORITY } from "pixi.js";
import type { EdgeMap, TerrainMap } from "@lancer-vtt/domain";
import type { GroundId } from "./tileArt";
import { CELL } from "../combatCells";
import {
  type ChunkKey,
  type WorldRect,
  HI_TEXTURE_BUDGET,
  MAX_DENSITY,
  allChunks,
  chunkBytes,
  chunkDensity,
  chunkRectOf,
  dirtyChunks,
  planEviction,
  visibleChunks,
} from "./chunks";
import type { Board } from "./geometry";
import { type Materials, sharedMaterials, sharedMaterialsMs } from "./materials";
import { chunkPixelSize, paintChunk } from "./paint";
import { type PreparedScene, prepareScene } from "./scene";

/** What `getTerrainBuild()` reports (`projects/terrain-tile-art.md` 4.6). */
export type TerrainBuildStats = {
  /** The last build that painted every chunk (first terrain, or a board resize), in ms. */
  coldMs: number | null;
  /** The last build that repainted only dirty chunks, in ms. */
  incrementalMs: number | null;
  /** Chunks the last build painted. */
  lastPainted: number;
  /** Main-thread time the last build spent painting, flush included; the rest of its ms is the
   *  frames rendered between chunk tasks and the upload render. */
  lastPaintMs: number;
  /** Builds completed since mount. */
  builds: number;
  /** True while a build or a 2× upgrade is queued. */
  busy: boolean;
  /** How long the atlas took to load, once per page. */
  atlasMs: number | null;
  chunks: number;
  hiChunks: number;
  /** GPU bytes of every chunk texture held (RGBA), 1× and 2×. */
  textureBytes: number;
};

type Tex = { texture: Texture; bytes: number };
type Chunk = { sprite: Sprite; base: Tex; hi: (Tex & { seen: number }) | null };


function paintTexture(scene: PreparedScene, mats: Materials, { key, density }: { key: ChunkKey; density: number }): Tex {
  const rect = chunkRectOf(scene.board, key);
  const size = chunkPixelSize(rect, density);
  const canvas = new OffscreenCanvas(size.width, size.height);
  paintChunk({ ctx: canvas.getContext("2d")!, rect, resolution: density }, scene, mats);
  // The bitmap snapshot flushes the canvas's deferred drawing, so the paint is really done here.
  const source = new ImageSource({ resource: canvas.transferToImageBitmap(), resolution: density });
  return { texture: new Texture({ source }), bytes: chunkBytes(rect, density) };
}

function destroyTex(t: Tex | null): void {
  if (!t) return;
  const resource = t.texture.source.resource as ImageBitmap;
  t.texture.destroy(true);
  resource.close();
}

export type TerrainLayer = {
  /** The board's terrain, walls and map ground; a ground change repaints every chunk. */
  setTerrain(board: Board, state: { terrain: TerrainMap; edges: EdgeMap; ground: GroundId }): void;
  /** A settled view: the world rect on screen and its device px per board px. */
  settle(view: WorldRect, density: number): void;
  stats(): TerrainBuildStats;
  destroy(): void;
};

export function createTerrainLayer({ container, ticker }: { container: Container; ticker: Ticker }): TerrainLayer {
  const chunks = new Map<ChunkKey, Chunk>();
  let board: Board | null = null;
  let current: { terrain: TerrainMap; edges: EdgeMap; ground: GroundId } | null = null;
  let scene: PreparedScene | null = null;
  let mats: Materials | null = null;

  // The build in flight: chunks still to paint, and 1× textures painted but not yet shown.
  const pending = new Set<ChunkKey>();
  const staged = new Map<ChunkKey, Tex>();
  let full = false;
  // A first paint has nothing to keep on screen, so it shows each chunk as it lands.
  let progressive = false;
  let buildStart = 0;
  let buildPaintedCount = 0;
  let buildPaintMs = 0;

  // 2× upgrades wanted by the last settled view.
  let view: { rect: WorldRect; density: number } | null = null;
  let upgrades: ChunkKey[] = [];
  let seenClock = 0;

  const stats: Omit<TerrainBuildStats, "busy" | "atlasMs" | "chunks" | "hiChunks" | "textureBytes"> = { coldMs: null, incrementalMs: null, lastPainted: 0, lastPaintMs: 0, builds: 0 };
  let timer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  const schedule = (): void => {
    if (timer === null && !destroyed) timer = setTimeout(step, 0);
  };

  function step(): void {
    timer = null;
    if (destroyed || !scene || !mats) return;
    const next = pending.values().next();
    if (!next.done) {
      pending.delete(next.value);
      const t0 = performance.now();
      const tex = paintTexture(scene, mats, { key: next.value, density: 1 });
      buildPaintMs += performance.now() - t0;
      buildPaintedCount++;
      if (progressive) install(next.value, tex);
      else staged.set(next.value, tex);
      if (pending.size === 0) swap();
      schedule();
      return;
    }
    const key = upgrades.shift();
    if (key !== undefined) {
      upgrade(key);
      schedule();
    }
  }

  function swap(): void {
    if (full) {
      // A resize drops the chunks the new board no longer has; every other one is replaced.
      const kept = new Set(allChunks(scene!.board));
      for (const [key, c] of chunks) {
        if (kept.has(key)) continue;
        c.sprite.destroy();
        destroyTex(c.base);
        destroyTex(c.hi);
        chunks.delete(key);
      }
    }
    for (const [key, tex] of staged) install(key, tex);
    const wasFull = full;
    const painted = buildPaintedCount;
    const paintMs = buildPaintMs;
    const started = buildStart;
    staged.clear();
    full = false;
    progressive = false;
    buildPaintedCount = 0;
    buildPaintMs = 0;
    // Time through the next render, which uploads the new textures.
    ticker.addOnce(
      () => {
        const ms = performance.now() - started;
        if (wasFull) stats.coldMs = ms;
        else stats.incrementalMs = ms;
        stats.lastPainted = painted;
        stats.lastPaintMs = paintMs;
        stats.builds++;
      },
      undefined,
      UPDATE_PRIORITY.LOW - 1,
    );
    if (view) planUpgrades();
  }

  function install(key: ChunkKey, tex: Tex): void {
    const c = chunks.get(key);
    if (c) {
      // The terrain under a stale 2× texture changed; it repaints on the next settle.
      destroyTex(c.base);
      destroyTex(c.hi);
      c.base = tex;
      c.hi = null;
      c.sprite.texture = tex.texture;
      return;
    }
    const rect = chunkRectOf(scene!.board, key);
    const sprite = new Sprite(tex.texture);
    sprite.position.set(rect.x0 * CELL, rect.y0 * CELL);
    container.addChild(sprite);
    chunks.set(key, { sprite, base: tex, hi: null });
  }

  function upgrade(key: ChunkKey): void {
    const c = chunks.get(key);
    if (!c || c.hi || pending.has(key) || !view || view.density < MAX_DENSITY) return;
    const bytes = chunkBytes(chunkRectOf(scene!.board, key), MAX_DENSITY);
    const visible = new Set(visibleChunks(scene!.board, view.rect));
    const held = [...chunks].filter(([, h]) => h.hi).map(([k, h]) => ({ key: k, bytes: h.hi!.bytes, seen: h.hi!.seen, visible: visible.has(k) }));
    const evict = planEviction(held, bytes, HI_TEXTURE_BUDGET);
    if (evict === null) {
      upgrades = [];
      return;
    }
    for (const k of evict) {
      const e = chunks.get(k)!;
      e.sprite.texture = e.base.texture;
      destroyTex(e.hi);
      e.hi = null;
    }
    c.hi = { ...paintTexture(scene!, mats!, { key, density: MAX_DENSITY }), seen: seenClock };
    c.sprite.texture = c.hi.texture;
  }

  /** Show each chunk at the settled density, and queue the visible ones that need 2×. */
  function planUpgrades(): void {
    if (!scene || !view) return;
    const hi = view.density >= MAX_DENSITY;
    const visible = visibleChunks(scene.board, view.rect);
    seenClock++;
    for (const key of visible) {
      const c = chunks.get(key);
      if (c?.hi) c.hi.seen = seenClock;
    }
    // Minified 2× textures alias more than 1× ones, so a zoomed-out view shows 1×.
    for (const c of chunks.values()) c.sprite.texture = hi && c.hi ? c.hi.texture : c.base.texture;
    upgrades = hi ? visible.filter((k) => !chunks.get(k)?.hi) : [];
    if (upgrades.length > 0) schedule();
  }

  function build(): void {
    if (!mats || !board || !current) return;
    const next = prepareScene({ board, terrain: current.terrain, edges: current.edges, ground: current.ground });
    scene = next;
    schedule();
  }

  return {
    setTerrain(nextBoard, { terrain, edges, ground }) {
      const resized = !board || board.width !== nextBoard.width || board.height !== nextBoard.height || current?.ground !== ground;
      const started = performance.now();
      if (resized || !current) {
        for (const t of staged.values()) destroyTex(t);
        staged.clear();
        pending.clear();
        // What the viewer is looking at paints first.
        for (const key of view ? visibleChunks(nextBoard, view.rect) : []) pending.add(key);
        for (const key of allChunks(nextBoard)) pending.add(key);
        full = true;
        progressive = chunks.size === 0;
        buildPaintedCount = 0;
        buildPaintMs = 0;
        buildStart = started;
      } else {
        const dirty = dirtyChunks(nextBoard, current, { terrain, edges });
        if (dirty.size === 0 && pending.size === 0) {
          current = { terrain, edges, ground };
          return;
        }
        if (pending.size === 0 && staged.size === 0) {
          buildStart = started;
          buildPaintedCount = 0;
          buildPaintMs = 0;
        }
        for (const key of dirty) {
          destroyTex(staged.get(key) ?? null);
          staged.delete(key);
          pending.add(key);
        }
      }
      board = nextBoard;
      current = { terrain, edges, ground };
      if (mats) {
        build();
        return;
      }
      void sharedMaterials()
        .then((m) => {
          if (destroyed) return;
          mats = m;
          // The build is timed from the atlas arriving; `atlasMs` reports the load itself.
          buildStart = Math.max(buildStart, performance.now());
          build();
        })
        .catch((e: unknown) => console.error("terrain atlas failed to load", e));
    },
    settle(rect, density) {
      view = { rect, density };
      planUpgrades();
    },
    stats() {
      let textureBytes = 0;
      let hiChunks = 0;
      for (const c of chunks.values()) {
        textureBytes += c.base.bytes + (c.hi?.bytes ?? 0);
        if (c.hi) hiChunks++;
      }
      for (const t of staged.values()) textureBytes += t.bytes;
      return { ...stats, busy: pending.size > 0 || upgrades.length > 0 || timer !== null, atlasMs: sharedMaterialsMs(), chunks: chunks.size, hiChunks, textureBytes };
    },
    destroy() {
      destroyed = true;
      if (timer !== null) clearTimeout(timer);
      for (const t of staged.values()) destroyTex(t);
      for (const c of chunks.values()) {
        c.sprite.destroy();
        destroyTex(c.base);
        destroyTex(c.hi);
      }
      chunks.clear();
    },
  };
}

/** The world rect the screen shows for a pan/zoom. */
export function screenWorldRect(view: { x: number; y: number; scale: number }, screen: { width: number; height: number }): WorldRect {
  return { x: -view.x / view.scale, y: -view.y / view.scale, w: screen.width / view.scale, h: screen.height / view.scale };
}

